/**
 * NewsletterGateHandler
 * Handles form submit, prevents default behavior,
 * and toggles a class on a target element.
 */
class ShowOnlyNewCustomerForm {
  /**
   * @param {Object} options
   * @param {string} options.formSelector - CSS selector for the form
   * @param {string} options.targetSelector - Element to receive the class
   * @param {string} options.className - Class to add
   * @param {boolean} [options.toggle=false] - Whether to toggle instead of just add
   */
  constructor({ formSelector, targetSelector, className, toggle = false }) {
    this.form = document.querySelector(formSelector);
    this.btn = this.form.querySelector(`button.only_new_customers`);
    this.target = document.querySelector(targetSelector);
    this.className = className;
    this.toggle = toggle;

    if (!this.form) {
      console.warn(`NewsletterGateHandler: Form not found (${formSelector})`);
      return;
    }

    if (!this.target) {
      console.warn(`NewsletterGateHandler: Target not found (${targetSelector})`);
      return;
    }

    this.handleSubmit = this.handleSubmit.bind(this);
    this.init();
  }

  init() {
    this.btn.addEventListener('click', this.handleSubmit);
  }

  handleSubmit(event) {
    event.preventDefault();

    if (this.toggle) {
      this.target.classList.toggle(this.className);
    } else {
      this.target.classList.add(this.className);
      setTimeout(() => {
       this.target.classList.add('visible');       
      }, 500);
    }

    // Placeholder for future backend validation
    // this.validateEmail();
  }

  destroy() {
    this.btn.removeEventListener('click', this.handleSubmit);
    this.btn.click();
  }
}

/**
 * PromotionUnlockForm
 * - Submits email to an endpoint to validate customer existence.
 * - On success: shows success message, hides form, updates wrapper classes,
 *   and triggers a callback to add the promo product.
 */
class PromotionUnlockForm {
  /**
   * @param {Object} options
   * @param {string|HTMLElement} options.root - Root element or selector that wraps the component
   * @param {string} options.endpoint - API endpoint that validates the email
   * @param {Function} [options.onUnlocked] - Callback fired when unlocked. Receives (payload, instance)
   * @param {string} [options.wrapperSelector] - Selector for the wrapper to remove classes from
   * @param {string[]} [options.removeWrapperClasses] - Classes to remove from wrapper
   * @param {string} [options.formSelector]
   * @param {string} [options.successSelector]
   * @param {string} [options.emailSelector]
   * @param {string} [options.hiddenClass]
   * @param {number} [options.timeoutMs]
   */
  constructor({
    root,
    endpoint,
    onUnlocked,
    wrapperSelector = '.validate-customer-form-wrap',
    removeWrapperClasses = ['show', 'visible'],
    formSelector = 'form.only-new-customer',
    successSelector = '[data-success]',
    emailSelector = 'input[name="customer[email]"]',
    hiddenClass = 'is-hidden',
    timeoutMs = 12000,
  }) {
    this.root = typeof root === 'string' ? document.querySelector(root) : root;
    this.endpoint = endpoint;
    this.onUnlocked = typeof onUnlocked === 'function' ? onUnlocked : null;

    this.wrapperSelector = wrapperSelector;
    this.removeWrapperClasses = removeWrapperClasses;

    this.formSelector = formSelector;
    this.successSelector = successSelector;
    this.emailSelector = emailSelector;
    this.hiddenClass = hiddenClass;
    this.timeoutMs = timeoutMs;
    this.addToCartFormSelector = 'product-info form[data-type="add-to-cart-form"]';

    if (!this.root) {
      console.warn('PromotionUnlockForm: root not found');
      return;
    }
    if (!this.endpoint) {
      console.warn('PromotionUnlockForm: endpoint is required');
      return;
    }

    this.form = this.root.querySelector(this.formSelector);
    this.addToCartForm = document.querySelector(this.addToCartFormSelector);
    this.successEl = this.root.querySelector(this.successSelector);
    this.emailInput = this.root.querySelector(this.emailSelector);
    this.wrapper = this.root.closest(this.wrapperSelector) || this.root.querySelector(this.wrapperSelector);

    if (!this.form || !this.emailInput || !this.successEl) {
      console.warn('PromotionUnlockForm: Missing required elements (form/email/success)');
      return;
    }

    this.submitBtn = this.form.querySelector('button[type="submit"]');

    this.handleSubmit = this.handleSubmit.bind(this);

    this.init();
  }

  init() {
    this.form.addEventListener('submit', this.handleSubmit);
  }

  destroy() {
    this.form.removeEventListener('submit', this.handleSubmit);
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;

    this.submitBtn.disabled = isLoading;
    this.submitBtn.setAttribute('aria-busy', String(isLoading));

    // Lightweight loading UX without forcing markup changes
    if (isLoading) {
      this._prevBtnText = this._prevBtnText || this.submitBtn.textContent;
      this.submitBtn.textContent = 'Validating...';
    } else if (this._prevBtnText) {
      this.submitBtn.textContent = this._prevBtnText;
    }
  }

  showError(message) {
    // Reuse a simple inline error node. Create it once.
    if (!this.errorEl) {
      this.errorEl = document.createElement('p');
      this.errorEl.className = 'subs-form__error';
      this.errorEl.setAttribute('role', 'alert');
      this.errorEl.style.marginTop = '10px';
      this.form.appendChild(this.errorEl);
    }

    this.errorEl.textContent = message || 'Something went wrong. Please try again.';
  }

  clearError() {
    if (this.errorEl) this.errorEl.textContent = '';
  }

  showSuccess() {
    // Show success message
    this.successEl.classList.remove(this.hiddenClass);

    // Hide the form
    this.form.classList.add(this.hiddenClass);

    // Remove wrapper classes (as requested)
    if (this.wrapper && Array.isArray(this.removeWrapperClasses)) {
     const submitButton = this.addToCartForm.querySelector(`button.only_new_customers`);
     console.log(`this.wrapper`,this.wrapper);
     submitButton.classList.remove('only_new_customers');
     setTimeout(() => {
      this.wrapper.classList.remove('visible');       
      setTimeout(() => {
       this.wrapper.classList.remove('show');
       
      }, 500);
     }, 2000);
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.clearError();

    const email = (this.emailInput.value || '').trim().toLowerCase();

    if (!email) {
      this.showError('Please enter your email.');
      return;
    }

    // Basic email validity (input already has type=email, but we guard anyway)
    if (!this.emailInput.checkValidity()) {
      this.showError('Please enter a valid email address.');
      return;
    }

    this.setLoading(true);

    try {
      const payload = await this.postWithTimeout(this.endpoint, {
        email,
        tags: this.getHiddenTagsValue(),
      });

      /**
       * Expected payload shape suggestion:
       * { ok: true, status: "UNLOCKED" }  -> unlock
       * { ok: false, status: "EXISTS" }   -> exists / block (optional)
       * { ok: false, message: "..." }     -> error
       */

      if (payload && payload.ok && payload.status === 'UNLOCKED') {
        this.showSuccess();

        // Fire callback to add product (your existing JS)
        if (this.onUnlocked) {
          await this.onUnlocked(payload, this);
        }
        return;
      }

      // Optional: handle "EXISTS" flow if you implement it later
      if (payload && payload.status === 'EXISTS') {
        this.showError('You’re already part of Big Hammer Wines.');
        return;
      }

      // Default fallback
      this.showError(payload?.message || 'We couldn’t validate your email. Please try again.');
    } catch (err) {
      this.showError(err?.message || 'Network error. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  getHiddenTagsValue() {
    const hidden = this.form.querySelector('input[type="hidden"][name="customer[tags]"]');
    return hidden ? hidden.value : '';
  }

  async postWithTimeout(url, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify(body),
      });

      // Non-2xx handling
      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const errJson = await res.json();
          msg = errJson?.message || msg;
        } catch (_) {}
        throw new Error(msg);
      }

      return await res.json();
    } finally {
      clearTimeout(timeout);
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const gate = new ShowOnlyNewCustomerForm({
    formSelector: 'product-info form[data-type="add-to-cart-form"]',
    targetSelector: ".validate-customer-form-wrap",
    className: "show",
    toggle: false,
  });

  const root = document.querySelector('.validate-customer-form-wrap');

  if (root) {   
   new PromotionUnlockForm({
     root,
     endpoint: "https://lifecycle-segmentation.onrender.com/api/promotions/unlock",
     onUnlocked: async () => {
      gate.destroy();
      console.log('onUnlocked!')
     },
   });
  }
});